import React, { useEffect, useState } from "react"
import { MetaTags } from "react-meta-tags"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { AvField, AvForm } from "availity-reactstrap-validation"
import { ApiPut, del, get, post } from "helpers/api_methods"
import { Notification } from "components/Common/Notification"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"

const ClientCategories = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState("")

  // Create Modal State
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)

  // Edit Modal State
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [editCategory, setEditCategory] = useState(null)
  const [editLoading, setEditLoading] = useState(false)

  // Delete Modal State
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleteCategory, setDeleteCategory] = useState(null)

  useEffect(() => {
    fetchCategories()
  }, [searchText])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const url = searchText
        ? `/order/client-category/?search=${encodeURIComponent(searchText)}`
        : `/order/client-category/`
      const res = await get(url)
      if (Array.isArray(res)) {
        setCategories(res)
      } else if (res && res.results) {
        setCategories(res.results)
      } else {
        setCategories([])
      }
    } catch (err) {
      console.error("Failed to fetch client categories", err)
    } finally {
      setLoading(false)
    }
  }

  // Handle Create Category
  const handleCreateSubmit = async (e, values) => {
    setCreateLoading(true)
    try {
      const res = await post("/order/client-category/", values)
      if (res && res.id) {
        Notification({
          type: "success",
          message: "Client Category created successfully",
          title: "Success",
        })
        setIsCreateOpen(false)
        fetchCategories()
      } else if (res && res.name) {
        Notification({
          type: "error",
          message: Array.isArray(res.name) ? res.name[0] : res.name,
          title: "Error",
        })
      }
    } catch (err) {
      Notification({
        type: "error",
        message: "Failed to create client category",
        title: "Error",
      })
    } finally {
      setCreateLoading(false)
    }
  }

  // Handle Edit Category
  const handleEditSubmit = async (e, values) => {
    if (!editCategory) return
    setEditLoading(true)
    try {
      const res = await ApiPut(`/order/client-category/${editCategory.id}/`, values)
      if (res && res.id) {
        Notification({
          type: "success",
          message: "Client Category updated successfully",
          title: "Success",
        })
        setIsEditOpen(false)
        setEditCategory(null)
        fetchCategories()
      } else if (res && res.name) {
        Notification({
          type: "error",
          message: Array.isArray(res.name) ? res.name[0] : res.name,
          title: "Error",
        })
      }
    } catch (err) {
      Notification({
        type: "error",
        message: "Failed to update client category",
        title: "Error",
      })
    } finally {
      setEditLoading(false)
    }
  }

  // Handle Delete Category
  const handleDeleteConfirm = async () => {
    if (!deleteCategory) return
    try {
      await del(`/order/client-category/${deleteCategory.id}/`)
      Notification({
        type: "success",
        message: "Client Category deleted successfully",
        title: "Success",
      })
      setIsDeleteOpen(false)
      setDeleteCategory(null)
      fetchCategories()
    } catch (err) {
      Notification({
        type: "error",
        message: "Failed to delete client category",
        title: "Error",
      })
    }
  }

  return (
    <>
      <MetaTags>
        <title>Client Categories | Indtech</title>
      </MetaTags>

      <div className="page-content">
        <Breadcrumbs title="Clients" breadcrumbItem="Client Categories" />

        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="mb-3 align-items-center">
                    <Col md={4}>
                      <div className="search-box me-2 d-inline-block w-100">
                        <div className="position-relative">
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Search category..."
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                          />
                          <i className="bx bx-search-alt search-icon" />
                        </div>
                      </div>
                    </Col>
                    <Col md={8} className="text-end">
                      <Button
                        color="success"
                        className="btn-rounded waves-effect waves-light"
                        onClick={() => setIsCreateOpen(true)}
                      >
                        <i className="mdi mdi-plus me-1" /> Add Client Category
                      </Button>
                    </Col>
                  </Row>

                  {loading ? (
                    <div className="text-center my-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table align-middle table-nowrap table-hover">
                        <thead className="table-light">
                          <tr>
                            <th scope="col" style={{ width: "70px" }}>
                              #
                            </th>
                            <th scope="col">Auto ID</th>
                            <th scope="col">Category Name</th>
                            <th scope="col">Date Added</th>
                            <th scope="col" className="text-end">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {categories && categories.length > 0 ? (
                            categories.map((cat, idx) => (
                              <tr key={cat.id || idx}>
                                <td>{idx + 1}</td>
                                <td>{cat.auto_id || "-"}</td>
                                <td>
                                  <strong>{cat.name}</strong>
                                </td>
                                <td>
                                  {cat.date_added
                                    ? new Date(cat.date_added).toLocaleDateString()
                                    : "-"}
                                </td>
                                <td className="text-end">
                                  <Button
                                    color="info"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => {
                                      setEditCategory(cat)
                                      setIsEditOpen(true)
                                    }}
                                  >
                                    <i className="mdi mdi-pencil" /> Edit
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => {
                                      setDeleteCategory(cat)
                                      setIsDeleteOpen(true)
                                    }}
                                  >
                                    <i className="mdi mdi-delete" /> Delete
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center py-4 text-muted">
                                No client categories found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Create Modal */}
      <Modal isOpen={isCreateOpen} toggle={() => setIsCreateOpen(!isCreateOpen)} centered>
        <ModalHeader toggle={() => setIsCreateOpen(!isCreateOpen)}>
          Create Client Category
        </ModalHeader>
        <ModalBody>
          <AvForm onValidSubmit={handleCreateSubmit}>
            <div className="mb-3">
              <label className="form-label" htmlFor="category-name-input">
                Category Name <span className="text-danger">*</span>
              </label>
              <AvField
                name="name"
                id="category-name-input"
                type="text"
                placeholder="Enter category name"
                validate={{
                  required: { value: true, errorMessage: "Please enter category name" },
                }}
              />
            </div>
            <div className="text-end mt-4">
              <Button
                type="button"
                color="secondary"
                className="me-2"
                onClick={() => setIsCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" color="success" disabled={createLoading}>
                {createLoading && (
                  <i className="bx bx-loader bx-spin font-size-16 align-middle me-2" />
                )}
                Save
              </Button>
            </div>
          </AvForm>
        </ModalBody>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} toggle={() => setIsEditOpen(!isEditOpen)} centered>
        <ModalHeader toggle={() => setIsEditOpen(!isEditOpen)}>
          Edit Client Category
        </ModalHeader>
        <ModalBody>
          <AvForm onValidSubmit={handleEditSubmit}>
            <div className="mb-3">
              <label className="form-label" htmlFor="edit-category-name-input">
                Category Name <span className="text-danger">*</span>
              </label>
              <AvField
                name="name"
                id="edit-category-name-input"
                type="text"
                value={editCategory?.name || ""}
                validate={{
                  required: { value: true, errorMessage: "Please enter category name" },
                }}
              />
            </div>
            <div className="text-end mt-4">
              <Button
                type="button"
                color="secondary"
                className="me-2"
                onClick={() => setIsEditOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" color="success" disabled={editLoading}>
                {editLoading && (
                  <i className="bx bx-loader bx-spin font-size-16 align-middle me-2" />
                )}
                Update
              </Button>
            </div>
          </AvForm>
        </ModalBody>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        show={isDeleteOpen}
        onCloseClick={() => setIsDeleteOpen(false)}
        onDeleteClick={handleDeleteConfirm}
      />
    </>
  )
}

export default ClientCategories
